import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

/**
 * ThemeProvider — light / dark mode.
 *
 * Persists in localStorage under `wikifi-theme`.
 * Adds/removes the `dark` class on <html>, which Tailwind's darkMode:'class'
 * picks up AND which the scoped overrides in index.css use to flip the site's
 * hardcoded #E7EAF0 / #0B1220 tokens to the dark palette (#1F2937 / #E7EAF0).
 */
export function ThemeProvider({ children }) {
  const [dark, setDark] = useState(() => {
    try {
      const saved = localStorage.getItem("wikifi-theme");
      if (saved === "dark") return true;
      if (saved === "light") return false;
      // First visit — follow the user's system preference.
      return window.matchMedia?.("(prefers-color-scheme: dark)").matches === true;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem("wikifi-theme", dark ? "dark" : "light"); } catch { /* ignore */ }
  }, [dark]);

  const toggle = useCallback(() => setDark((v) => !v), []);
  const value = useMemo(() => ({ dark, toggle }), [dark, toggle]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
